import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Menu from "@material-ui/core/Menu";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import AddIcon from "@material-ui/icons/Add";
import BoltIcon from "@material-ui/icons/OfflineBolt";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import HistoryIcon from "@material-ui/icons/History";
import clsx from "clsx";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import useMenuStyles from "../../components/menuStyles";
import ConnectorIcon from "../Connectors/ConnectorIcon";
import { byType } from "../Connectors/catalog";

// Subscrições de EVENTOS do agente (handoff §1.4): "ao <evento> → enviar para
// <destino>". Mecanismo do sistema (não a IA): assíncrono, observador.
const EVENT_LABEL = {
  "conversa.encerrada": "Ao encerrar o atendimento",
  "turno.concluido": "A cada turno (pergunta + resposta)",
  "handoff.solicitado": "Ao transferir para humano",
  "conversa.iniciada": "Ao iniciar a conversa",
  "ferramenta.chamada": "Ao usar uma ferramenta",
  // curinga: UMA subscrição que espelha a conversa inteira (todos os eventos).
  espelhar: "A cada mensagem — espelhar a conversa inteira",
};
const DEST_LABEL = {
  conector: "Conector (envia pra fora, HTTP)",
  dataset: "Tabela (grava um registro)",
  fluxo: "Fluxo (dispara uma automação)",
};
const NEW_DATASET = "__new__";
// tipos de conector com integração de evento padrão (sabem receber o envelope).
const INTEGRATED_CONNECTOR_TYPES = ["atende"];

const selectMenuProps = menuCls => ({
  classes: { paper: menuCls.selectPaper },
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
});

const useStyles = makeStyles(theme => {
  const t = k => (theme.app ? theme.app[k] : undefined);
  return {
    wrap: { padding: "20px 0" },
    header: { display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 18 },
    desc: { flex: 1, fontSize: 13.5, color: t("textTertiary") || "#929AA4", lineHeight: 1.5 },
    primaryBtn: {
      flexShrink: 0, textTransform: "none", fontSize: 13, fontWeight: 600, color: "#fff",
      background: t("dark") || "#1B222A", borderRadius: 8, padding: "9px 18px", boxShadow: "none",
      "&:hover": { background: t("ink") || "#0F141A", boxShadow: "none" },
    },
    empty: {
      background: "#fff", border: `1px solid ${t("border") || "#E6E9ED"}`, borderRadius: 16,
      padding: theme.spacing(7, 3), textAlign: "center",
    },
    emptyIcon: {
      width: 46, height: 46, borderRadius: 999, background: t("lineSoft") || "#F1F4F2",
      display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12,
      "& svg": { fontSize: 22, color: t("textTertiary") || "#929AA4" },
    },
    emptyText: { color: t("textSecondary") || "#5B6470", fontSize: 13.5 },
    list: { display: "flex", flexDirection: "column", gap: 10 },
    card: {
      display: "flex", alignItems: "center", gap: 14, background: "#fff",
      border: `1px solid ${t("border") || "#E6E9ED"}`, borderRadius: 16, padding: "14px 18px",
    },
    cardMain: { flex: 1, minWidth: 0 },
    phrase: { fontSize: 14, color: t("ink") || "#2c3036", lineHeight: 1.45 },
    sub: { fontSize: 12.5, color: t("textTertiary") || "#929AA4", marginTop: 3 },
    badge: { fontSize: 11, fontWeight: 700, color: "#5B6470", background: "#EEF1F4", borderRadius: 6, padding: "2px 7px", marginLeft: 8 },
    badgeWarn: { fontSize: 11, fontWeight: 700, color: "#9A6A00", background: "#FFF3D6", borderRadius: 6, padding: "2px 7px", marginLeft: 8 },
    destLine: { display: "inline-flex", alignItems: "center", gap: 6, verticalAlign: "middle" },
    actions: { display: "flex", alignItems: "center", gap: 2, flexShrink: 0 },
    kebab: { color: t("textTertiary") || "#929AA4" },
    deliveries: { background: "#F6F7F9", borderRadius: 10, padding: "8px 12px", margin: "2px 0 0" },
    delivery: { fontSize: 12.5, padding: "3px 0", color: "#2c3036" },
    // modal
    optIcon: { display: "inline-flex", alignItems: "center", gap: 10 },
    field: { marginTop: 16 },
    hint: { fontSize: 12.5, color: t("textTertiary") || "#929AA4", marginTop: 6 },
  };
});

const AgentEventos = ({ agentId, connectors = [], projectId }) => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [flows, setFlows] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [rowMenu, setRowMenu] = useState(null); // {anchor, sub}
  const [openId, setOpenId] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [testing, setTesting] = useState(null);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const blankForm = { event: "conversa.encerrada", destinoType: "conector", destinoRef: "", samplingPercent: 100, comHandoff: false };
  const [form, setForm] = useState(blankForm);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/agents/${agentId}/event-subscriptions`);
      setSubs(data.subscriptions || []);
      setEvents(data.events || []);
    } catch (err) {
      toastError(err);
    }
  }, [agentId]);

  const loadDatasets = useCallback(async () => {
    try {
      const { data } = await api.get("/datasets", { params: { projectId } });
      setDatasets(data.datasets || []);
    } catch (e) {
      /* ignore */
    }
  }, [projectId]);

  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await api.get("/applications", { params: { projectId } });
        setFlows((data.applications || []).filter(a => !["agent", "tool", "collector"].includes(a.triggerType)));
      } catch (e) {
        /* ignore */
      }
      loadDatasets();
    })();
  }, [load, loadDatasets, projectId]);

  // só conectores com integração de evento PADRÃO viram destino (hoje: Numera
  // Atende). Os genéricos não sabem receber o envelope, então não aparecem.
  const eventConnectors = connectors.filter(c => INTEGRATED_CONNECTOR_TYPES.includes(c.type));
  const destOptions = form.destinoType === "conector" ? eventConnectors : form.destinoType === "fluxo" ? flows : datasets;
  const destTypes = Object.keys(DEST_LABEL);
  // os eventos reais (do backend) + o curinga "espelhar" (espelha a conversa inteira).
  const realEvents = events.length ? events : Object.keys(EVENT_LABEL).filter(e => e !== "espelhar");
  const whenOptions = [...realEvents, "espelhar"];

  const openNew = () => {
    setForm(blankForm);
    setOpen(true);
  };

  const add = async () => {
    if (!form.destinoRef) {
      toast.warn("Escolha o destino.");
      return;
    }
    setSaving(true);
    try {
      const creatingTable = form.destinoType === "dataset" && form.destinoRef === NEW_DATASET;
      await api.post(`/agents/${agentId}/event-subscriptions`, {
        event: form.event,
        destinoType: form.destinoType,
        destinoRef: creatingTable ? NEW_DATASET : String(form.destinoRef),
        createDataset: creatingTable || undefined,
        samplingPercent: Number(form.samplingPercent) || 100,
        filter: form.comHandoff ? { comHandoff: true } : {},
      });
      toast.success(creatingTable ? "Envio criado e tabela nova gerada." : "Envio criado.");
      setOpen(false);
      if (creatingTable) loadDatasets();
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const del = async id => {
    setRowMenu(null);
    try {
      await api.delete(`/event-subscriptions/${id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const test = async id => {
    setTesting(id);
    try {
      const { data } = await api.post(`/event-subscriptions/${id}/test`);
      if (data.ok) toast.success("Teste entregue ao destino ✓");
      else toast.error(`Falhou${data.httpStatus ? ` (HTTP ${data.httpStatus})` : ""}: ${data.detail || "erro"}`);
      if (openId === id) loadDeliveries(id);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(null);
    }
  };

  const loadDeliveries = async id => {
    try {
      const { data } = await api.get(`/event-subscriptions/${id}/deliveries`);
      setDeliveries(data.deliveries || []);
      setOpenId(id);
    } catch (err) {
      toastError(err);
    }
  };

  const destName = s => {
    const list = s.destinoType === "conector" ? connectors : s.destinoType === "fluxo" ? flows : datasets;
    const hit = list.find(x => String(x.id) === String(s.destinoRef));
    return hit ? hit.name : `#${s.destinoRef}`;
  };
  const destConnType = s => {
    if (s.destinoType !== "conector") return null;
    const hit = connectors.find(x => String(x.id) === String(s.destinoRef));
    return hit ? hit.type : null;
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.header}>
        <div className={classes.desc}>
          Eventos são automáticos: o <b>sistema</b> dispara (não a IA), sem atrasar nem atrapalhar a conversa. Use para
          avaliação de qualidade, BI ou CRM. Ex.: <i>ao encerrar o atendimento → enviar para um conector</i>.
        </div>
        <Button className={classes.primaryBtn} startIcon={<AddIcon />} onClick={openNew}>Nova subscrição</Button>
      </div>

      {subs.length === 0 ? (
        <div className={classes.empty}>
          <div className={classes.emptyIcon}><BoltIcon /></div>
          <div className={classes.emptyText}>
            Nenhum envio configurado ainda. Clique em <b>"Nova subscrição"</b> para enviar eventos deste agente a um
            conector, tabela ou fluxo.
          </div>
        </div>
      ) : (
        <div className={classes.list}>
          {subs.map(s => {
            const connType = destConnType(s);
            const cat = connType ? byType(connType) : null;
            return (
              <div key={s.id}>
                <div className={classes.card}>
                  <div className={classes.cardMain}>
                    <div className={classes.phrase}>
                      <b>{EVENT_LABEL[s.event] || s.event}</b> → {DEST_LABEL[s.destinoType]}
                      {": "}
                      <span className={classes.destLine}>
                        {cat ? <ConnectorIcon icon={cat.icon} name={cat.name} color={cat.color} size={18} /> : null}
                        <b>{destName(s)}</b>
                      </span>
                      {s.inherited ? <span className={classes.badge}>DA CONTA</span> : null}
                      {s.active === false ? <span className={classes.badgeWarn}>PAUSADA</span> : null}
                    </div>
                    <div className={classes.sub}>
                      {s.samplingPercent < 100 ? `${s.samplingPercent}% das conversas` : "100% das conversas"}
                      {s.filter && s.filter.comHandoff ? " · só com transferência" : ""}
                    </div>
                  </div>
                  {!s.inherited && (
                    <div className={classes.actions}>
                      <Button size="small" onClick={() => test(s.id)} disabled={testing === s.id}>{testing === s.id ? "..." : "Testar"}</Button>
                      <IconButton size="small" className={classes.kebab} title="Entregas" onClick={() => (openId === s.id ? setOpenId(null) : loadDeliveries(s.id))}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" className={classes.kebab} onClick={e => setRowMenu({ anchor: e.currentTarget, sub: s })}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </div>
                  )}
                </div>
                {openId === s.id && (
                  <div className={classes.deliveries}>
                    {deliveries.length === 0 ? (
                      <div className={classes.emptyText} style={{ padding: "4px 0" }}>Nenhuma entrega ainda.</div>
                    ) : (
                      deliveries.map(d => (
                        <div key={d.id} className={classes.delivery}>
                          <span style={{ color: d.status === "sent" ? "#1E9E62" : "#D14343", fontWeight: 700 }}>
                            {d.status === "sent" ? "✓ enviado" : "✕ falhou"}
                          </span>
                          {d.httpStatus ? ` · HTTP ${d.httpStatus}` : ""}
                          {d.detail ? ` · ${String(d.detail).slice(0, 80)}` : ""}
                          <span style={{ color: "#929AA4", marginLeft: 8 }}>{new Date(d.at).toLocaleString("pt-BR")}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Menu
        anchorEl={rowMenu ? rowMenu.anchor : null}
        open={!!rowMenu}
        onClose={() => setRowMenu(null)}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem className={clsx(menu.item, menu.danger)} onClick={() => del(rowMenu.sub.id)}>
          <DeleteIcon fontSize="small" /> Remover
        </MenuItem>
      </Menu>

      {/* Modal — nova subscrição */}
      <Dialog open={open} onClose={() => (saving ? undefined : setOpen(false))} maxWidth="sm" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>Nova subscrição de evento</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            label="Quando"
            fullWidth
            variant="outlined"
            size="small"
            value={form.event}
            onChange={e => setForm({ ...form, event: e.target.value })}
            SelectProps={{ MenuProps: selectMenuProps(menu) }}
          >
            {whenOptions.map(ev => (
              <MenuItem key={ev} value={ev}>{EVENT_LABEL[ev] || ev}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Enviar para"
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            value={form.destinoType}
            onChange={e => setForm({ ...form, destinoType: e.target.value, destinoRef: "" })}
            SelectProps={{ MenuProps: selectMenuProps(menu) }}
          >
            {destTypes.map(d => (
              <MenuItem key={d} value={d}>{DEST_LABEL[d]}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Destino"
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            value={form.destinoRef}
            onChange={e => setForm({ ...form, destinoRef: e.target.value })}
            SelectProps={{ MenuProps: selectMenuProps(menu) }}
          >
            <MenuItem value="">— selecione —</MenuItem>
            {form.destinoType === "dataset" && (
              <MenuItem value={NEW_DATASET}>
                <span className={classes.optIcon}><AddIcon fontSize="small" /> Criar nova tabela</span>
              </MenuItem>
            )}
            {destOptions.map(o => {
              if (form.destinoType === "conector") {
                const cat = byType(o.type);
                return (
                  <MenuItem key={o.id} value={o.id}>
                    <span className={classes.optIcon}>
                      {cat ? <ConnectorIcon icon={cat.icon} name={cat.name} color={cat.color} size={20} /> : null}
                      {o.name}
                    </span>
                  </MenuItem>
                );
              }
              return <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>;
            })}
          </TextField>
          {form.destinoType === "dataset" && form.destinoRef === NEW_DATASET && (
            <div className={classes.hint}>
              O sistema cria a tabela sozinho, já com as colunas deste evento (evento, desfecho, turnos, duração e as
              saídas do agente).
            </div>
          )}

          <TextField
            type="number"
            label="% das conversas"
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            value={form.samplingPercent}
            onChange={e => setForm({ ...form, samplingPercent: e.target.value })}
            inputProps={{ min: 1, max: 100 }}
          />

          <FormControlLabel
            style={{ marginTop: 8 }}
            control={<Checkbox color="primary" checked={form.comHandoff} onChange={e => setForm({ ...form, comHandoff: e.target.checked })} />}
            label="Só quando houve transferência"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={add} color="primary" variant="contained" disableElevation disabled={saving || !form.destinoRef}>
            {saving ? "Salvando…" : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AgentEventos;
